import { Divider, Spin } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'react-infinite-scroll-component'

import {
  Group,
  ParentCommentsFragment,
  PostType,
  RequestedPostFragment,
  SectionType,
  WhoCanPost,
  useGetViewerQuery,
} from '@/generated/graphql'
import { PostCard } from '@/modules/post/post-card'
import { TopPost } from '@/modules/post/top-post/top-post.component'

import { PostEditorContainer } from './post-editor/post-editor.container'
import {
  PostsSortBy,
  useFetchNewPosts,
  useGetTimelinePosts,
  useLastUnreadPost,
  useLocationKeyChange,
} from './post.hooks'
import { PostsSorter } from './posts-actions/posts-actions.component'
import s from './posts.module.less'

interface PostsContainerProps {
  group?: Group
  userId?: number
  isAllPosts?: boolean
  sectionId?: SectionType
  showCreatePost?: boolean
}

export const PostsContainer: React.FC<PostsContainerProps> = ({
  showCreatePost = true,
  group,
  sectionId,
  userId,
  isAllPosts = false,
}) => {
  const [t] = useTranslation()
  const postTypes = group ? Object.values(PostType).filter((i) => i != PostType.Thanks) : Object.values(PostType)
  const [sortBy, setSortBy] = React.useState<PostsSortBy>('createdAtDesc')

  const { data: me } = useGetViewerQuery()

  const { posts, pageInfo, loading, loadingMore, error, refetch, onLoadMore } = useGetTimelinePosts({
    isAllPosts,
    group,
    sectionId,
    userId,
    sortBy: sortBy,
    postTypes,
  })

  const isSynthesis = !userId && !group && !isAllPosts && sectionId === SectionType.Synthesis

  const lastUnreadPost = useLastUnreadPost(posts)

  useFetchNewPosts({
    skip: !isSynthesis,
    fetch(newEndCursor) {
      if (newEndCursor !== pageInfo.endCursor) {
        refetch()
      }
    },
  })

  useLocationKeyChange(() => refetch())

  /*
   * Hooks End Here
   */
  if (error) {
    return null
  }

  const canPost = !group
    ? false
    : ((whoCanPost: WhoCanPost) => {
        switch (whoCanPost) {
          case WhoCanPost.Anyone:
            return true
          case WhoCanPost.Admin:
            return !!me!.viewer.roles.find((r) => r.groupId === group.groupId)
          case WhoCanPost.Member:
            return group.isFollowing
        }
      })(group.whoCanPost)

  const loadingComponent = (loading || loadingMore) && (
    <div className={s.loading}>
      <Spin size='large' />
    </div>
  )

  const infiniteScroll = loading ? (
    loadingComponent
  ) : (
    <InfiniteScroll
      dataLength={posts.length + (group || sectionId ? 1 : 0)}
      loader={loadingComponent}
      className={s.infiniteScroll}
      hasMore={pageInfo.hasPreviousPage || pageInfo.hasNextPage}
      next={onLoadMore}
    >
      {(group || sectionId) && <TopPost sectionId={sectionId} groupId={group?.groupId} />}
      {posts.map((p: RequestedPostFragment & ParentCommentsFragment) => (
        <React.Fragment key={p.postId}>
          <PostCard post={p} isSection={!!sectionId} isGroup={!!group} isUser={!!userId} isAllPosts={isAllPosts} />
          {lastUnreadPost && lastUnreadPost.postId === p.postId && (
            <Divider className={s.readDivider}>{t('post.feedsLastReadHere')}</Divider>
          )}
        </React.Fragment>
      ))}
    </InfiniteScroll>
  )

  const showCreate = ((group ? canPost : isSynthesis) || sectionId) && !isAllPosts && showCreatePost

  return (
    <div className={s.container}>
      {showCreate && <PostEditorContainer group={group} sectionId={sectionId} />}
      {isAllPosts && <PostsSorter sortBy={sortBy} setSortBy={setSortBy} />}
      {infiniteScroll}
    </div>
  )
}
